import { useState, useEffect } from 'react';
import { X, Mail, User, Phone, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  INQUIRY_TOPICS,
  generateSubject,
  generateEmailBody,
  generateEmailHTML,
  getDefaultTopics,
  getProgramContactEmail,
  formatProgramType
} from '@/utils/emailTemplates';

const ContactProgramModal = ({ 
  isOpen, 
  onClose, 
  program, 
  selectedChild = null,
  user 
}) => {
  // State management
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [personalMessage, setPersonalMessage] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [parentPhone, setParentPhone] = useState(user?.phone || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customTopicText, setCustomTopicText] = useState('');
  
  // Get program contact email
  const programEmail = getProgramContactEmail(program);
  
  // Initialize default topics when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaults = getDefaultTopics(program);
      setSelectedTopics(defaults);
      setError(null);
      setShowPreview(false);
      setPersonalMessage('');
      setCustomTopicText('');
    }
  }, [isOpen, program]);
  
  // Prepare email data
  const emailData = {
    programName: program.name || program.title,
    programType: formatProgramType(program),
    childName: selectedChild?.name || selectedChild?.first_name,
    childAge: selectedChild?.age,
    childInterests: selectedChild?.interests || [],
    parentName: user?.name || user?.full_name || 'A parent',
    parentEmail: user?.email,
    parentPhone,
    personalMessage,
    selectedTopics
  };
  
  const emailSubject = generateSubject(emailData);
  const emailBody = generateEmailBody(emailData);
  const emailHTML = generateEmailHTML(emailData);
  
  // Toggle topic selection
  const toggleTopic = (topicLabel) => {
    setSelectedTopics(prev => {
      if (prev.includes(topicLabel)) {
        return prev.filter(t => t !== topicLabel);
      } else {
        return [...prev, topicLabel];
      }
    });
  };
  
  // Handle "Other" topic with custom text
  const handleOtherTopic = () => {
    if (customTopicText.trim()) {
      toggleTopic(customTopicText.trim());
      setCustomTopicText('');
    }
  };
  
  // Send inquiry
  const handleSend = async () => {
    // Validation
    if (!programEmail) {
      setError('No contact email available for this program. Please try contacting them directly.');
      return;
    }
    
    if (selectedTopics.length === 0) {
      setError('Please select at least one topic to inquire about.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Get program ID (handle different ID formats)
      const programId = program.id || program.place_id || program.camp_id || null;
      
      // Save inquiry to database
      const { data: inquiryData, error: dbError } = await supabase
        .from('program_inquiries')
        .insert({
          user_id: user.id,
          child_id: selectedChild?.id || null,
          program_id: programId,
          program_name: program.name || program.title,
          recipient_email: programEmail,
          email_subject: emailSubject,
          email_body: emailBody,
          selected_topics: selectedTopics,
          status: 'sent',
          sent_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (dbError) throw dbError;
      
      // Send email via Edge Function (with CC to ourselves!)
      const { data: emailData, error: emailError } = await supabase.functions.invoke('send-inquiry-email', {
        body: {
          to: programEmail,
          subject: emailSubject,
          body: emailBody,
          html: emailHTML,
          replyTo: user.email,
          programId: programId,
          userId: user.id,
          inquiryId: inquiryData.id
        }
      });
      
      if (emailError) {
        throw new Error(emailError.message || 'Failed to send email');
      }
      
      // Success! Close modal and show success state
      onClose('success', {
        programName: program.name || program.title,
        email: programEmail
      });
      
    } catch (err) {
      console.error('Failed to send inquiry:', err);
      setError(err.message || 'Failed to send inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={() => !loading && onClose()}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Contact {program.name || program.title}
                </h2>
                <p className="text-sm text-gray-500">
                  {programEmail ? `Will send to: ${programEmail}` : 'No email on file'}
                </p>
              </div>
            </div>
            <button
              onClick={() => !loading && onClose()}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            
            {/* Error message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            
            {/* No email warning */}
            {!programEmail && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-medium mb-2">
                  ⚠️ No contact email available
                </p>
                <p className="text-sm text-amber-700">
                  We don't have an email address for this program yet. You can try:
                </p>
                <ul className="mt-2 text-sm text-amber-700 space-y-1">
                  {program.website && (
                    <li>
                      • Visit their website:{' '}
                      <a 
                        href={program.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="underline hover:text-amber-900"
                      >
                        {program.website}
                      </a>
                    </li>
                  )}
                  {program.phone && (
                    <li>• Call them: {program.phone}</li>
                  )}
                </ul>
              </div>
            )}
            
            {/* Topics selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                What would you like to know about?
                <span className="text-gray-500 font-normal ml-1">(Select all that apply)</span>
              </label>
              
              <div className="space-y-2">
                {INQUIRY_TOPICS.map(topic => (
                  <label
                    key={topic.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedTopics.includes(topic.label)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTopics.includes(topic.label)}
                      onChange={() => toggleTopic(topic.label)}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <span className={`flex-1 text-sm ${
                      selectedTopics.includes(topic.label)
                        ? 'text-gray-900 font-medium'
                        : 'text-gray-700'
                    }`}>
                      {topic.label}
                    </span>
                    {selectedTopics.includes(topic.label) && (
                      <Check className="w-5 h-5 text-purple-600" />
                    )}
                  </label>
                ))}
                
                {/* Custom "Other" topic */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customTopicText}
                    onChange={(e) => setCustomTopicText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleOtherTopic()}
                    placeholder="Other (type and press Enter)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleOtherTopic}
                    disabled={!customTopicText.trim()}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
            
            {/* Child selection (if available) */}
            {selectedChild && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Inquiry for:
                </label>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedChild.name || selectedChild.first_name}
                      {selectedChild.age && `, Age ${selectedChild.age}`}
                    </p>
                    {selectedChild.interests && selectedChild.interests.length > 0 && (
                      <p className="text-xs text-gray-500">
                        Interests: {selectedChild.interests.slice(0, 3).join(', ')}
                        {selectedChild.interests.length > 3 && ` +${selectedChild.interests.length - 3} more`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Personal message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add a personal message <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <textarea
                value={personalMessage}
                onChange={(e) => setPersonalMessage(e.target.value)}
                placeholder="Hi! I'm interested in summer programs for my daughter. We're looking for something that focuses on..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>
            
            {/* Phone number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={parentPhone}
                  onChange={(e) => setParentPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Email preview toggle */}
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center justify-between w-full p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
            >
              <span className="text-sm font-medium text-gray-700">
                📧 Preview Email
              </span>
              {showPreview ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            
            {/* Email preview content */}
            {showPreview && (
              <div className="p-4 bg-white border-2 border-gray-200 rounded-lg space-y-3">
                <div className="pb-3 border-b border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">To:</p>
                  <p className="text-sm font-medium text-gray-900">{programEmail || 'Not available'}</p>
                </div>
                
                <div className="pb-3 border-b border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">CC:</p>
                  <p className="text-sm font-medium text-gray-900">inquiries@send.mysummersquad.com</p>
                  <p className="text-xs text-gray-500 mt-1">
                    We receive responses to improve our database
                  </p>
                </div>
                
                <div className="pb-3 border-b border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Subject:</p>
                  <p className="text-sm font-medium text-gray-900">{emailSubject}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 mb-2">Message:</p>
                  <div className="bg-gray-50 p-4 rounded border border-gray-200">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                      {emailBody}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between gap-4 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => !loading && onClose()}
              disabled={loading}
              className="px-6 py-2.5 text-gray-700 hover:text-gray-900 font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSend}
              disabled={loading || !programEmail || selectedTopics.length === 0}
              className="px-8 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  Send Inquiry
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactProgramModal;

